import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Prohibit } from 'phosphor-react';

import { MOTIVOS_DISPENSA } from '../legalizacaoPlaybook';

const LIMITE = 300;

interface DispensarItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Rótulo do item sendo dispensado — o operador confirma que é o certo. */
  item?: string;
  onConfirm: (motivo: string, obs: string) => void;
}

/**
 * Dispensa de um item do checklist, com justificativa.
 *
 * Nasceu do apontamento da Juliana sobre a SEFAZ — obrigatória só para quem tem
 * Inscrição Estadual, então empresa de serviço puro nunca passa por lá — mas a decisão
 * foi generalizar: vale para qualquer item de qualquer etapa.
 *
 * ⚠️ O que isto resolve NÃO é destravar o fluxo. Os itens de "Demais órgãos" já são
 * `req: false` e nunca prenderam ninguém. O que faltava era RASTREABILIDADE: um item
 * desmarcado é ambíguo entre "não se aplica a esta empresa" e "o colaborador esqueceu",
 * e quem abre o card depois não tem como saber qual dos dois.
 *
 * Por isso, e ao contrário do motivo no arquivamento (opcional de propósito, para não
 * empurrar o operador a digitar qualquer coisa), aqui a justificativa é OBRIGATÓRIA:
 * uma dispensa sem motivo não desfaz ambiguidade nenhuma — só troca um estado ambíguo
 * por outro.
 */
export const DispensarItemModal: React.FC<DispensarItemModalProps> = ({
  isOpen,
  onClose,
  item,
  onConfirm,
}) => {
  const [motivo, setMotivo] = useState('');
  const [obs, setObs] = useState('');

  useEffect(() => {
    if (isOpen) {
      setMotivo('');
      setObs('');
    }
  }, [isOpen]);

  const escolhido = MOTIVOS_DISPENSA.find(m => m.key === motivo);
  // "Outro motivo" sem texto não diz nada — é o único caso em que a opção sozinha
  // não basta.
  const faltaObs = !!escolhido?.exigeObs && obs.trim().length === 0;
  const podeConfirmar = !!escolhido && !faltaObs;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="xl">
        <ModalHeader display="flex" alignItems="center" gap={2} fontSize="16px">
          <Prohibit size={20} weight="duotone" />
          Dispensar item
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            {item && (
              <Text fontSize="13px" color="gray.600">
                O item <b>{item}</b> deixa de ser cobrado nesta etapa e passa a mostrar o motivo no
                lugar da marcação.
              </Text>
            )}

            <FormControl isRequired>
              <FormLabel fontSize="13px" mb={2}>
                Motivo
              </FormLabel>
              <RadioGroup value={motivo} onChange={setMotivo}>
                <Stack spacing={2}>
                  {MOTIVOS_DISPENSA.map(m => (
                    <Radio key={m.key} value={m.key} size="sm">
                      <Text fontSize="13px">{m.label}</Text>
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>

            <FormControl isRequired={!!escolhido?.exigeObs}>
              <FormLabel fontSize="13px" mb={1}>
                Observação
              </FormLabel>
              <Textarea
                rows={3}
                fontSize="13px"
                placeholder="Ex.: empresa presta apenas serviço, sem Inscrição Estadual."
                value={obs}
                onChange={e => setObs(e.target.value.slice(0, LIMITE))}
              />
              <FormHelperText fontSize="11px">
                {escolhido?.exigeObs ? 'Obrigatória para "Outro motivo".' : 'Opcional.'}
                {obs.length > 0 && ` · ${obs.length}/${LIMITE}`}
              </FormHelperText>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" borderRadius="full" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="orange"
            borderRadius="full"
            isDisabled={!podeConfirmar}
            onClick={() => onConfirm(motivo, obs.trim())}
          >
            Dispensar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
